/**
 * profileContext — manages the current user's profile state
 * Provides: profile, loading, error
 * Actions: fetchProfile, updateProfile, updateProfileLocal, clearProfile,
 *          upsertProfile, updateMeasurement, userRelation, updateInvite,
 *          getUserConnections, getBlockedUsers, getChartData, clearError
 *
 * GetMyProfile runs only after auth restore completes and the user is
 * authenticated — never during splash / anonymous startup.
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  type PropsWithChildren,
} from 'react';
import { storage } from '../platform/storage';
import { STORAGE_KEYS } from '../constants';
import { analytics, ANALYTICS_EVENTS, resolveDistinctId } from '../analytics';
import { APP_ENV } from '../config/env';
import { getApiErrorMessage } from '../utils/apiErrorHandler';
import { toWeightScaleUnitId, type WeightUnitCode } from '../utils/weightUtils';
import profileService from '../api/services/profileService';
import {
  mapApiProfileToProfile,
  mapProfileToUpdateRequest,
} from '../utils/userProfileMapper';
import { useAuth } from './AuthContext';
import { eventBus } from '../platform/eventBus';
import { USER_SESSION_CLEARED_EVENT } from '../api/auth/clearUserSession';
import { BUDDY_PREFERENCES_UPDATED_EVENT } from '../utils/questionnaire';
import type { ApiError } from '../types/api';
import type { Profile, ProfileContextValue, ProfileState } from '../types';
import type {
  UserProfileUpsertRequest,
  UserRelationAction,
  GetUserConnectionsRequest,
  UserConnectionItem,
  ChartDataRequest,
  ChartData,
  BlockedUserItem,
  FollowedAiAgent,
  GetUserBuddyPreferencesResponse,
  UpdateProfilePrivacySettingsResponse,
} from '../types/userProfile';

// ─── Initial State ────────────────────────────────────────────────────────────
const initialState: ProfileState = {
  profile: null,
  tabs: [],
  reportOptions: [],
  buddyPreferences: null,
  loading: false,
  error: null,
};

// ─── Reducer ──────────────────────────────────────────────────────────────────
const PROFILE_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_PROFILE: 'SET_PROFILE',
  SET_TABS: 'SET_TABS',
  SET_REPORT_OPTIONS: 'SET_REPORT_OPTIONS',
  SET_BUDDY_PREFERENCES: 'SET_BUDDY_PREFERENCES',
  CLEAR_PROFILE: 'CLEAR_PROFILE',
  CLEAR_ERROR: 'CLEAR_ERROR',
} as const;

type ProfileAction =
  | { type: typeof PROFILE_ACTIONS.SET_LOADING; payload: boolean }
  | { type: typeof PROFILE_ACTIONS.SET_ERROR; payload: string }
  | { type: typeof PROFILE_ACTIONS.SET_PROFILE; payload: Partial<Profile> }
  | { type: typeof PROFILE_ACTIONS.SET_TABS; payload: string[] }
  | { type: typeof PROFILE_ACTIONS.SET_BUDDY_PREFERENCES; payload: GetUserBuddyPreferencesResponse | null }
  | { type: typeof PROFILE_ACTIONS.CLEAR_PROFILE }
  | { type: typeof PROFILE_ACTIONS.CLEAR_ERROR };

const profileReducer = (
  state: ProfileState,
  action: ProfileAction,
): ProfileState => {
  switch (action.type) {
    case PROFILE_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload, error: null };
    case PROFILE_ACTIONS.SET_ERROR:
      return { ...state, loading: false, error: action.payload };
    case PROFILE_ACTIONS.SET_PROFILE:
      return {
        ...state,
        loading: false,
        error: null,
        profile: { ...(state.profile ?? {}), ...action.payload } as Profile
      };
    case PROFILE_ACTIONS.SET_TABS:
      return { ...state, loading: false, error: null, tabs: action.payload };
    case PROFILE_ACTIONS.SET_BUDDY_PREFERENCES:
      return { ...state, buddyPreferences: action.payload };
    case PROFILE_ACTIONS.CLEAR_PROFILE:
      return { ...initialState };
    case PROFILE_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    default:
      return state;
  }
};

/** Drop transient upload-only fields that must never hit storage / cache. */
const toPersistableProfile = (profile: Profile): Profile => {
  const persistable = { ...profile };
  delete persistable.avatarBlob;
  return persistable as Profile;
};

/** Enrich PostHog person properties once profile fields are available. */
const syncAnalyticsIdentityFromProfile = (
  profile: Partial<Profile> | Profile | null | undefined,
): void => {
  if (!profile) {
    return;
  }
  const distinctId = resolveDistinctId(profile.userGuid, profile.userId);
  if (!distinctId) {
    return;
  }
  analytics.identify(distinctId, {
    userId: profile.userId ?? null,
    userGuid: profile.userGuid ?? null,
    firstName: profile.firstName ?? null,
    userName: profile.userName ?? null,
    email: profile.email ?? null,
    appEnv: APP_ENV,
  });
};

// ─── Context ──────────────────────────────────────────────────────────────────
const profileContext = createContext<ProfileContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────
export const ProfileProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [state, dispatch] = useReducer(profileReducer, initialState);
  const { isAuthenticated, initializing } = useAuth();
  const wasAuthenticatedRef = useRef(false);
  const profileRef = useRef(state.profile);

  useEffect(() => {
    profileRef.current = state.profile;
  }, [state.profile]);

  // ── fetchProfile ────────────────────────────────────────────────────────────
  /**
   * GET /api/v1/UserProfile/me
   * Loads the authenticated user's profile and caches it to storage.
   *
   * NOTE: Response shape is partially inferred (API collection shows empty body).
   * The mapper will populate only the fields the backend returns; others remain
   * at their defaults until the backend confirms the full schema.
   */
  const fetchProfile = useCallback(async () => {
    const [accessToken, refreshToken] = await Promise.all([
      storage.getItem<string>(STORAGE_KEYS.AUTH_TOKEN),
      storage.getItem<string>(STORAGE_KEYS.REFRESH_TOKEN),
    ]);
    if (!accessToken && !refreshToken) {
      return;
    }

    dispatch({ type: PROFILE_ACTIONS.SET_LOADING, payload: true });
    try {
      const { data: rawProfile } = await profileService.getProfile();

      if (rawProfile) {
        const mapped = mapApiProfileToProfile(rawProfile);
        if ((!mapped.avatar || (typeof mapped.avatar === 'object' && 'uri' in mapped.avatar && !mapped.avatar.uri)) && profileRef.current?.avatar) {
          mapped.avatar = profileRef.current.avatar;
        }
        await storage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(mapped));
        // Persist userGuid so the edit-journey submit flow can resolve it
        // for users who logged in (not signed up) and have no cached GUID.
        if (mapped.userGuid) {
          await storage.setItem(STORAGE_KEYS.USER_GUID, mapped.userGuid);
        }
        
        // Store the AI Agent conversation ID globally on initial load
        if (mapped.followedAiAgent?.conversationId) {
          await storage.setItem(STORAGE_KEYS.ROBI_CONVERSATION_ID, mapped.followedAiAgent.conversationId);
        }
        
        dispatch({
          type: PROFILE_ACTIONS.SET_PROFILE,
          payload: mapped as Partial<Profile>,
        });
        syncAnalyticsIdentityFromProfile(mapped);
      } else {
        // Response was successful but empty — stop loading without error
        dispatch({ type: PROFILE_ACTIONS.SET_LOADING, payload: false });
      }
    } catch (error) {
      dispatch({
        type: PROFILE_ACTIONS.SET_ERROR,
        payload: getApiErrorMessage(error as ApiError),
      });
    }
  }, []);

  const fetchStaticData = useCallback(async () => {
    try {
      const [tabsRes] = await Promise.all([
        profileService.getProfileTabs(),
      ]);
      dispatch({ type: PROFILE_ACTIONS.SET_TABS, payload: tabsRes.data });
    } catch (error) {
      console.warn('Failed to load static profile data', error);
    }
  }, []);

  // ── clearProfile ────────────────────────────────────────────────────────────
  const clearProfile = useCallback(async () => {
    await Promise.all([
      storage.removeItem(STORAGE_KEYS.USER_PROFILE),
      storage.removeItem(STORAGE_KEYS.BUDDY_PREFERENCES),
    ]);
    dispatch({ type: PROFILE_ACTIONS.CLEAR_PROFILE });
  }, []);

  // Hydrate cache + fetch GetMyProfile only once auth restore finishes and
  // the user is signed in. Guests / onboarding must not hit GetMyProfile.
  useEffect(() => {
    if (initializing) {
      return;
    }

    let cancelled = false;

    const syncProfileWithAuth = async (): Promise<void> => {
      if (!isAuthenticated) {
        if (wasAuthenticatedRef.current) {
          wasAuthenticatedRef.current = false;
          await clearProfile();
        } else {
          dispatch({ type: PROFILE_ACTIONS.CLEAR_PROFILE });
        }
        return;
      }

      wasAuthenticatedRef.current = true;

      try {
        const [storedProfile, storedPrefs] = await Promise.all([
          storage.getItem<Profile>(STORAGE_KEYS.USER_PROFILE),
          storage.getItem<GetUserBuddyPreferencesResponse | string>(STORAGE_KEYS.BUDDY_PREFERENCES),
        ]);
        if (!cancelled && storedProfile) {
          const parsed =
            typeof storedProfile === 'string'
              ? (JSON.parse(storedProfile) as Profile)
              : storedProfile;
          dispatch({ type: PROFILE_ACTIONS.SET_PROFILE, payload: parsed });
          syncAnalyticsIdentityFromProfile(parsed);
        }
        if (!cancelled && storedPrefs) {
          const parsedPrefs =
            typeof storedPrefs === 'string'
              ? (JSON.parse(storedPrefs) as GetUserBuddyPreferencesResponse)
              : storedPrefs;
          dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: parsedPrefs });
        }
      } catch {
        // Ignore storage read errors
      }

      if (cancelled) return;
      await fetchProfile();
      if (cancelled) return;
      await fetchStaticData();
    };

    syncProfileWithAuth().catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [
    initializing,
    isAuthenticated,
    fetchProfile,
    fetchStaticData,
    clearProfile,
  ]);

  useEffect(() => {
    const sub1 = eventBus.addListener(USER_SESSION_CLEARED_EVENT, () => {
      wasAuthenticatedRef.current = false;
      dispatch({ type: PROFILE_ACTIONS.CLEAR_PROFILE });
    });
    const sub2 = eventBus.addListener(
      BUDDY_PREFERENCES_UPDATED_EVENT,
      (prefs: GetUserBuddyPreferencesResponse) => {
        dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: prefs });
      },
    );
    return () => {
      sub1.remove();
      sub2.remove();
    };
  }, []);

  // ── updateProfile ───────────────────────────────────────────────────────────
  /**
   * PUT /api/v1/UserProfile/me
   * Converts the `Profile` partial into the API request shape via the mapper,
   * then optimistically updates local state and storage.
   *
   * NOTE: API request body is not fully documented. Fields that cannot be
   * mapped are omitted to avoid accidentally resetting backend data.
   */
  const updateProfile = useCallback(
    async (
      payload: Partial<Profile>,
    ): Promise<import('axios').AxiosResponse<UpdateProfilePrivacySettingsResponse>> => {
      dispatch({ type: PROFILE_ACTIONS.SET_LOADING, payload: true });
      try {
        const apiPayload = mapProfileToUpdateRequest(payload);
        const response = (await profileService.updateProfile(
          apiPayload as Parameters<typeof profileService.updateProfile>[0],
        )) as import('axios').AxiosResponse<UpdateProfilePrivacySettingsResponse>;

        const resData = response?.data;
        const updatedProfile = resData?.data?.profile;
        // Exclude avatarBlob from the merge — it is upload-only and not part of Profile cache.
        const profilePatchClone = { ...payload };
        delete profilePatchClone.avatarBlob;
        let updatedData = { ...state.profile, ...profilePatchClone } as Profile;

        if (payload.name) {
          const parts = payload.name.trim().split(/\s+/).filter(Boolean);
          updatedData = {
            ...updatedData,
            name: payload.name.trim(),
            firstName: parts[0] || updatedData.firstName,
            lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
          };
        }

        if (updatedProfile) {
          const mapped = mapApiProfileToProfile(updatedProfile);
          if ((!mapped.avatar || (typeof mapped.avatar === 'object' && 'uri' in mapped.avatar && !mapped.avatar.uri)) && state.profile?.avatar) {
            mapped.avatar = state.profile.avatar;
          }
          updatedData = {
            ...state.profile,
            ...mapped,
            // Prefer the name the user just saved if the API echo is empty.
            name: mapped.name?.trim() || updatedData.name,
            firstName: mapped.firstName || updatedData.firstName,
            lastName: mapped.lastName ?? updatedData.lastName,
          } as Profile;
        }

        const persistable = toPersistableProfile(updatedData);
        await storage.setItem(
          STORAGE_KEYS.USER_PROFILE,
          JSON.stringify(persistable),
        );

        dispatch({ type: PROFILE_ACTIONS.SET_PROFILE, payload: persistable });
        syncAnalyticsIdentityFromProfile(persistable);
        return response;
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        throw error;
      }
    },
    [state.profile],
  );

  // ── updateProfileLocal ──────────────────────────────────────────────────────
  const updateProfileLocal = useCallback(
    async (payload: Partial<Profile>) => {
      const profilePatchClone = { ...payload };
      delete profilePatchClone.avatarBlob;
      const persistable = toPersistableProfile({
        ...state.profile,
        ...profilePatchClone,
      } as Profile);
      await storage.setItem(
        STORAGE_KEYS.USER_PROFILE,
        JSON.stringify(persistable),
      );
      dispatch({ type: PROFILE_ACTIONS.SET_PROFILE, payload: persistable });
      syncAnalyticsIdentityFromProfile(persistable);
    },
    [state.profile],
  );

  // ── upsertProfile ───────────────────────────────────────────────────────────
  /**
   * POST /api/v1/UserProfile/upsert
   * Creates or replaces the profile record (used post-registration).
   * After a successful upsert, re-fetches the profile to sync state.
   */
  const upsertProfile = useCallback(
    async (payload: UserProfileUpsertRequest) => {
      dispatch({ type: PROFILE_ACTIONS.SET_LOADING, payload: true });
      try {
        await profileService.upsertProfile(payload);
        // Re-fetch to get the full server-stored profile
        await fetchProfile();
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        throw error;
      }
    },
    [fetchProfile],
  );

  // ── updateMeasurement ───────────────────────────────────────────────────────
  /**
   * POST /api/v1/UserProfile/UpdateUserMeasurement
   * Logs a new weight entry. Also updates the local profile's currentWeight.
   */
  const updateMeasurement = useCallback(
    async (currentWeight: number, unit?: WeightUnitCode) => {
      dispatch({ type: PROFILE_ACTIONS.SET_LOADING, payload: true });
      try {
        await profileService.updateMeasurement({
          currentWeight,
          ...(unit ? { weightScaleUnit: toWeightScaleUnitId(unit) } : {}),
        });
        // Optimistically reflect the new weight in local state
        dispatch({
          type: PROFILE_ACTIONS.SET_PROFILE,
          payload: { currentWeight, ...(unit ? { unit } : {}) } as Partial<Profile>,
        });
        // No weight value in analytics props (health privacy).
        analytics.trackOnce(
          STORAGE_KEYS.ANALYTICS_FIRST_WEIGHT_LOGGED,
          ANALYTICS_EVENTS.ACTIVATION_FIRST_WEIGHT_LOGGED,
        );
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        throw error;
      }
    },
    [],
  );

  // ── userRelation ────────────────────────────────────────────────────────────
  /**
   * POST /api/v1/UserProfile/UserRelation
   * Fires a social action (Follow, UnFollow, Block, UnBlock, Agent) against another user.
   * Returns the response data so callers can access conversationId (Agent action).
   */
  const userRelation = useCallback(
    async (relatedUserId: number, action: UserRelationAction, buddyDetails?: Partial<FollowedAiAgent>) => {
      try {
        const { data } = await profileService.userRelation({ relatedUserId, action });
        
        if (action === 'Agent') {
          if (data?.conversationId) {
            await storage.setItem(STORAGE_KEYS.ROBI_CONVERSATION_ID, data.conversationId);
          }
          
          if (state.profile) {
            const updatedProfile = {
              ...state.profile,
              followedAiAgent: {
                ...state.profile.followedAiAgent,
                ...(buddyDetails || {}),
                ...(data?.conversationId ? { conversationId: data.conversationId } : {}),
              },
            } as Profile;
            
            await storage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(updatedProfile));
            dispatch({ type: PROFILE_ACTIONS.SET_PROFILE, payload: updatedProfile });
          }
        }

        if (action === 'Follow' || action === 'UnFollow') {
          await fetchProfile();
        }
        
        return data;
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        throw error;
      }
    },
    [state.profile, fetchProfile],
  );

  // ── updateInvite ────────────────────────────────────────────────────────────
  /**
   * PUT /api/v1/UserProfile/Invite
   * Accepts or declines a buddy invite from the given sender.
   *
   * TODO: Confirm valid `status` integer values with backend.
   */
  const updateInvite = useCallback(
    async (senderId: number, status: number) => {
      try {
        await profileService.updateInvite({ senderId, status });
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        throw error;
      }
    },
    [],
  );

  // ── getUserConnections ──────────────────────────────────────────────────────
  /**
   * POST /api/v1/UserProfile/GetUserConnections
   * Returns a page of the user's social connections.
   */
  const getUserConnections = useCallback(
    async (payload: GetUserConnectionsRequest): Promise<UserConnectionItem[]> => {
      try {
        const { data } = await profileService.getUserConnections(payload);
        return data?.users ?? [];
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        return [];
      }
    },
    [],
  );

  // ── getChartData ────────────────────────────────────────────────────────────
  /**
   * POST /api/v1/UserProfile/GetChartData
   * Fetches chart data for the given date range and metric.
   *
   * TODO: Confirm valid `periodicalType` and `chartName` values with backend.
   */
  const getChartData = useCallback(
    async (payload: ChartDataRequest): Promise<ChartData | null> => {
      try {
        const { data } = await profileService.getChartData(payload);
        return data?.data ?? null;
      } catch (error) {
        dispatch({
          type: PROFILE_ACTIONS.SET_ERROR,
          payload: getApiErrorMessage(error as ApiError),
        });
        return null;
      }
    },
    [],
  );

  // ── getBlockedUsers ──────────────────────────────────────────────────────────
  /**
   * GET /api/v1/UserProfile/UserRelation?relation=block
   * Returns the list of users blocked by the authenticated user.
   */
  const getBlockedUsers = useCallback(async (): Promise<BlockedUserItem[]> => {
    try {
      const { data } = await profileService.getBlockedUsers();
      return data?.users ?? [];
    } catch (error) {
      dispatch({
        type: PROFILE_ACTIONS.SET_ERROR,
        payload: getApiErrorMessage(error as ApiError),
      });
      return [];
    }
  }, []);

  // ── fetchBuddyPreferences ───────────────────────────────────────────────────
  /**
   * GET /api/v1/UserProfile/GetUserBuddyPreferences
   * Loads the user's buddy preferences questionnaire answers and caches them in Store / storage.
   * If already available in Store and forceRefresh is false, skips the network request.
   */
  const fetchBuddyPreferences = useCallback(
    async (forceRefresh = false): Promise<GetUserBuddyPreferencesResponse | null> => {
      if (!forceRefresh && state.buddyPreferences) {
        return state.buddyPreferences;
      }

      try {
        const response = await profileService.getUserBuddyPreferences();
        if (
          response.data &&
          (response.data.success || response.data.statusCode === 200 || response.data.mainGoalId)
        ) {
          const prefs = response.data;
          dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: prefs });
          await storage.setItem(STORAGE_KEYS.BUDDY_PREFERENCES, JSON.stringify(prefs));
          return prefs;
        }
        dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: null });
        await storage.removeItem(STORAGE_KEYS.BUDDY_PREFERENCES);
        return null;
      } catch (error: any) {
        const status = error?.response?.status || error?.status || error?.statusCode;
        if (status === 404) {
          dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: null });
          await storage.removeItem(STORAGE_KEYS.BUDDY_PREFERENCES);
          return null;
        }
        return state.buddyPreferences ?? null;
      }
    },
    [state.buddyPreferences],
  );

  const setBuddyPreferences = useCallback((prefs: GetUserBuddyPreferencesResponse | null) => {
    dispatch({ type: PROFILE_ACTIONS.SET_BUDDY_PREFERENCES, payload: prefs });
    if (prefs) {
      storage.setItem(STORAGE_KEYS.BUDDY_PREFERENCES, JSON.stringify(prefs)).catch(() => undefined);
    } else {
      storage.removeItem(STORAGE_KEYS.BUDDY_PREFERENCES).catch(() => undefined);
    }
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: PROFILE_ACTIONS.CLEAR_ERROR });
  }, []);

  return (
    <profileContext.Provider
      value={{
        ...state,
        fetchProfile,
        fetchBuddyPreferences,
        setBuddyPreferences,
        updateProfile,
        updateProfileLocal,
        clearProfile,
        clearError,
        upsertProfile,
        updateMeasurement,
        userRelation,
        updateInvite,
        getUserConnections,
        getChartData,
        getBlockedUsers,
      }}>
      {children}
    </profileContext.Provider>
  );
};

export const useProfile = (): ProfileContextValue => {
  const profile = useContext(profileContext);
  if (!profile) {
    throw new Error('useProfile must be used inside <ProfileProvider>');
  }
  return profile;
};

export default profileContext;
